import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { FileText, Shield, Code2, ArrowLeft } from 'lucide-react';

const Section = ({ kicker, title, children }) => (
  <section className="max-w-3xl mx-auto px-6 md:px-8 py-8">
    {kicker && <p className="text-xs tracking-[0.25em] uppercase text-charcoal/60 mb-2">{kicker}</p>}
    <h2 className="font-heading font-bold text-2xl md:text-3xl text-charcoal mb-4">{title}</h2>
    <div className="text-charcoal/80 leading-relaxed space-y-3 text-sm md:text-base">{children}</div>
  </section>
);

const Whitepaper = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="whitepaper-page">
      <Header />
      <main className="pb-20">
        <header className="border-b-2 border-charcoal/10 bg-surface-alt/40">
          <div className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-charcoal/60 hover:text-charcoal mb-6" data-testid="whitepaper-back">
              <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
            </Link>
            <p className="text-xs tracking-[0.25em] uppercase text-charcoal/60 mb-3">Whitepaper v1.0 — Février 2026</p>
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-charcoal tracking-tight mb-4">
              Standard Tagora ID
            </h1>
            <p className="text-charcoal/70 text-base md:text-lg leading-relaxed">
              Spécification d'un identifiant universel de propriété pour objets physiques.
              Anonyme. Infalsifiable. Vérifiable hors-ligne.
            </p>
          </div>
        </header>

        <Section kicker="01" title="Résumé exécutif">
          <p>
            Tagora propose un standard ouvert de certification de propriété pour tout objet physique,
            indépendamment de sa catégorie ou de son fabricant. La plateforme génère des identifiants
            uniques (Tagora&nbsp;ID) au format <code className="bg-surface-alt px-1.5 py-0.5 rounded text-[13px]">TGR-XXXX-XXXX-XXXX-XXXX</code>
            dont l'intégrité peut être vérifiée localement par n'importe quel terminal, sans dépendance
            réseau ni intermédiaire commercial.
          </p>
          <p>
            Le protocole vise trois usages&nbsp;: l'émission par les fabricants, la vérification par
            les tiers (assureurs, douanes, plateformes de revente), et le transfert anonyme entre
            propriétaires successifs.
          </p>
        </Section>

        <Section kicker="02" title="Format de l'identifiant">
          <p>
            Le Tagora&nbsp;ID utilise l'alphabet <strong>Crockford Base32</strong> (32 caractères,
            exclusion de I, L, O, U pour éviter toute ambiguïté visuelle) et un préfixe fixe <code className="bg-surface-alt px-1.5 py-0.5 rounded text-[13px]">TGR-</code>.
            Chaque identifiant comporte 16 caractères de données, dont le dernier est un <strong>checksum
            mod-37</strong> permettant la détection offline d'erreurs de saisie ou de falsification simple.
          </p>
          <p>
            Espace d'adressage&nbsp;: <code className="bg-surface-alt px-1.5 py-0.5 rounded text-[13px]">32^15 ≈ 3,7 × 10²²</code> identifiants uniques —
            soit 4&nbsp;trillions d'objets par humain sur Terre.
          </p>
        </Section>

        <Section kicker="03" title="Sécurité &amp; confidentialité">
          <ul className="list-disc pl-5 space-y-2">
            <li>Communications chiffrées en transit via TLS 1.3. Les messages anonymes transitent via un canal éphémère côté serveur, sans jamais exposer l'identité du propriétaire à l'expéditeur.</li>
            <li>Le scan d'un Tagora&nbsp;ID ne révèle <em>jamais</em> l'identité du propriétaire&nbsp;: la mise en relation passe par un canal anonyme éphémère (72 heures par défaut).</li>
            <li>Le protocole est résistant à la falsification grâce au checksum cryptographique + journal d'audit horodaté de chaque transfert.</li>
          </ul>
        </Section>

        <Section kicker="04" title="Conformité réglementaire">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>RGPD&nbsp;:</strong> base légale "intérêt légitime" pour la mise en relation anonyme, données personnelles minimisées.</li>
            <li><strong>Compatible DPP EU 2026&nbsp;:</strong> infrastructure d'identification et de vérification compatible avec le règlement européen <em>Ecodesign for Sustainable Products</em> (ESPR) et le Digital Product Passport. Les champs DPP étendus (composition, empreinte environnementale, instructions de recyclage, conformité REACH) sont sur la roadmap 2026-2027 et seront implémentés en accompagnement des premiers clients dont la catégorie de produits sera concernée par un acte délégué.</li>
            <li><strong>Open standard&nbsp;:</strong> spécification publiée sous licence permissive, aucun lock-in propriétaire.</li>
          </ul>
        </Section>

        <Section kicker="05" title="Gouvernance &amp; roadmap">
          <p>
            Tagora opère le service par défaut, mais la spécification est ouverte&nbsp;: tout fabricant
            peut implémenter son propre émetteur conforme au standard. Une fondation de gouvernance
            indépendante sera mise en place en 2026 pour faire évoluer le protocole de manière neutre.
          </p>
        </Section>

        <div className="max-w-3xl mx-auto px-6 md:px-8 pt-8">
          <div className="card-brutal bg-white p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div>
              <p className="font-bold text-charcoal mb-1 inline-flex items-center gap-2"><Shield className="w-4 h-4 text-primary" /> Aller plus loin</p>
              <p className="text-sm text-charcoal/70">Consultez la documentation technique pour intégrer Tagora dans votre système.</p>
            </div>
            <Link to="/developers" className="btn-primary inline-flex items-center justify-center gap-2 shrink-0" data-testid="whitepaper-cta-docs">
              <Code2 className="w-4 h-4" /> Documentation API
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Whitepaper;
